import { FC, MouseEvent } from 'react';
import styled, { css } from 'styled-components';

const ActionsLeft = styled.div`
  display: flex;
  gap: 12px;
  opacity: 0;
  transition: opacity ease-out 0.2s;
`;

const Holder = styled.div<{ $isSelected?: boolean }>`
  user-select: none;
  position: relative;
  width: 100%;
  cursor: pointer;
  transition: all ease-out 0.2s;
  img {
    width: 100%;
  }
  &:hover {
    ${ActionsLeft} {
      opacity: 1;
    }
    &:before {
      opacity: 1;
    }
  }
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    opacity: 0;
    z-index: 0;
    transition: opacity ease-out 0.2s;
  }
  &::after {
    content: '';
    position: absolute;
    width: calc(100% + 8px);
    height: calc(100% + 8px);
    border-radius: 2px;
    border: solid 3px transparent;
    left: -4px;
    top: -4px;
    transition: border-color ease-out 0.2s;
  }
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      &::after {
        border-color: #2558ca;
      }
    `}
`;

const Actions = styled.div`
  position: absolute;
  left: 16px;
  bottom: 16px;
  width: calc(100% - 32px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1;
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

const Img = styled.img`
  transition: all ease-out 0.2s;
  display: block;
  width: 100%;
`;

interface Props {
  image: string;
  index: number;
  selectedImages: number[];
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  onLightboxClick: () => void;
}

const ResultImage: FC<Props> = ({
  image,
  index,
  selectedImages,
  setSelectedImages,
  onLightboxClick,
}) => {
  const OnClick = () => {
    setSelectedImages((prev) => {
      if (!prev.includes(index)) {
        return [...prev, index];
      }
      return prev.filter((element) => element !== index);
    });
  };

  const isSelected = selectedImages.includes(index);

  return (
    <Holder $isSelected={isSelected} onClick={OnClick}>
      <Img src={image} alt="generated" />
      <Actions>
        <ActionsLeft>
          <ActionButton
            onClick={(e) => {
              e.stopPropagation();
              onLightboxClick();
            }}
          >
            <svg
              width="17"
              height="18"
              viewBox="0 0 17 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.57888 14.5383C11.1335 14.5383 14.0152 11.6454 14.0152 8.07677C14.0152 4.50816 11.1335 1.61523 7.57888 1.61523C4.02421 1.61523 1.14258 4.50816 1.14258 8.07677C1.14258 11.6454 4.02421 14.5383 7.57888 14.5383Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.1309 12.646L15.8548 16.3845"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ActionButton>
          <ActionButton
            onClick={(e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
            as="a"
            download={`image-${index + 1}.jpg`}
            href={image}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6354 10.0008C16.6354 13.665 13.665 16.6354 10.0008 16.6354C6.33663 16.6354 3.36621 13.665 3.36621 10.0008C3.36621 6.33663 6.33663 3.36621 10.0008 3.36621C13.665 3.36621 16.6354 6.33663 16.6354 10.0008Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
              />
              <path
                d="M7.38965 10.4668L10.0005 13.0776L12.6113 10.4668"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 6.92383V13.0777"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ActionButton>
        </ActionsLeft>
      </Actions>
    </Holder>
  );
};

export default ResultImage;
